import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import ChartFrame from "@/components/ChartFrame";
import { TrendUp, Users, Robot, CheckCircle } from "@phosphor-icons/react";

const STAGE_COLORS = {
  nuevo: "#3B82F6",
  contactado: "#A855F7",
  calificado: "#5E6AD2",
  negociacion: "#F59E0B",
  ganado: "#10B981",
  perdido: "#EF4444",
};
const STAGE_KEYS = ["nuevo", "contactado", "calificado", "negociacion", "ganado", "perdido"];

export default function Analytics() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/summary").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-8 text-[#A1A1AA] text-sm">{t("dash_loading")}</div>;

  const funnelData = STAGE_KEYS.map((k) => ({
    name: t(`stage_${k}`), value: data.funnel[k] || 0, color: STAGE_COLORS[k],
  }));

  return (
    <div className="p-8 max-w-7xl mx-auto fade-in">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">{t("ana_kicker")}</div>
        <h1 className="font-display text-4xl font-medium tracking-tighter">{t("ana_title")}</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Insights.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: t("dash_contacts"), value: data.total_contacts, icon: Users },
          { label: `${t("dash_messages")} IA`, value: data.ai_messages, icon: Robot, sub: `/ ${data.total_messages}` },
          { label: t("dash_conversion"), value: `${data.conversion_rate}%`, icon: TrendUp },
          { label: t("stage_ganado"), value: data.won, icon: CheckCircle },
        ].map((s) => (
          <div key={s.label} className="border border-[#27272A] bg-[#121214] rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{s.label}</div>
              <s.icon size={16} className="text-[#71717A]" />
            </div>
            <div className="font-display text-3xl font-light tracking-tighter text-white mt-4">{s.value}</div>
            {s.sub && <div className="text-xs text-[#71717A] mt-1">{s.sub}</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Embudo</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-4">Distribucion del pipeline</h3>
          <ChartFrame height={288}>
            {({ width, height }) => (
              <BarChart data={funnelData} layout="vertical" margin={{ left: 10 }} width={width} height={height}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal={false} />
                <XAxis type="number" stroke="#71717A" fontSize={11} />
                <YAxis dataKey="name" type="category" stroke="#71717A" fontSize={11} width={100} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {funnelData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            )}
          </ChartFrame>
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Volumen</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-4">Mensajes por dia</h3>
          <ChartFrame height={288}>
            {({ width, height }) => (
              <BarChart data={data.messages_series} width={width} height={height}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickFormatter={(d) => new Date(d).toLocaleDateString("es-MX", { day: "numeric" })} />
                <YAxis stroke="#71717A" fontSize={11} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="messages" fill="#5E6AD2" radius={[4, 4, 0, 0]} />
              </BarChart>
            )}
          </ChartFrame>
        </div>
      </div>

      <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Insight</div>
        <h3 className="font-display text-xl font-medium tracking-tight">Desempeno IA</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-5">
          <div>
            <div className="text-xs text-[#71717A]">% mensajes enviados por IA</div>
            <div className="font-display text-3xl font-light tracking-tighter text-white mt-2">
              {data.total_messages ? Math.round((data.ai_messages / data.total_messages) * 100) : 0}%
            </div>
          </div>
          <div>
            <div className="text-xs text-[#71717A]">Promedio msg/contacto</div>
            <div className="font-display text-3xl font-light tracking-tighter text-white mt-2">
              {data.total_contacts ? (data.total_messages / data.total_contacts).toFixed(1) : 0}
            </div>
          </div>
          <div>
            <div className="text-xs text-[#71717A]">Citas / contactos</div>
            <div className="font-display text-3xl font-light tracking-tighter text-white mt-2">
              {data.total_contacts ? Math.round((data.appointments / data.total_contacts) * 100) : 0}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
